// Practice log and the progress derived from it.
// A visitor's log lives in localStorage; an account's log lives on the server. Logging in uploads the
// local log once, so nothing earned before creating the account is lost.

import { store } from "../state/index.ts";
import * as api from "../services/api.ts";
import * as storage from "../services/storage.ts";
import { computeProgress } from "../domain/progressRules.ts";

const IMPORT_BATCH_SIZE = 10;   // the server accepts at most this many practices per request

let log = [];

function isLoggedIn() {
    return !!store.get('user.currentUser');
}

function publish(source) {
    store.batch((set) => {
        set('progress.source', source);
        set('progress.summary', computeProgress(log));
        set('progress.loaded', true);
    });
}

/** Loads the log for the current user state. Call after the auth state is known. */
export async function load() {
    if (!isLoggedIn()) {
        log = storage.loadPracticeLog();
        publish("local");
        return;
    }
    await importLocalLog();
    try {
        log = await api.fetchPracticeProgress();
        publish("server");
    } catch (err) {
        console.warn("[progress] Failed to load progress from the server", err);
        log = [];
        publish("server");
    }
}

/** Moves the practices made before logging in into the account. Safe to repeat: the server skips known ids. */
async function importLocalLog() {
    const local = storage.loadPracticeLog();
    if (!local.length) return;
    let imported = 0;
    let remaining = local;
    try {
        while (remaining.length) {
            const batch = remaining.slice(0, IMPORT_BATCH_SIZE);
            try {
                imported += (await api.importPracticeSessions(batch)).imported || 0;
            } catch (err) {
                // A batch the server refuses as invalid will never be accepted: let it go instead of
                // retrying it on every visit. Anything else (network, session) is worth another try.
                if (err.status !== 400) throw err;
                console.warn("[progress] Dropping practices the server refused", err);
            }
            remaining = remaining.slice(IMPORT_BATCH_SIZE);
        }
    } catch (err) {
        console.warn("[progress] Failed to import local practices, keeping the rest on this device", err);
    }
    if (remaining.length) {
        storage.savePracticeLog(remaining);
    } else {
        storage.clearPracticeLog();
    }
    if (imported) store.set('progress.imported', imported);
}

/**
 * Records a finished practice and returns the progress before and after it.
 * @param entry  practice log entry (see progressRules.ts)
 * @param moves  per-move details, only sent to the server
 */
export function record(entry, moves) {
    const before = store.get('progress.summary') || computeProgress([]);
    // The moves travel with the entry, so a visitor's practices keep their detail when imported later
    const full = { ...entry, moves };
    if (entry.counted) {
        log = [...log, full];
    }
    if (isLoggedIn()) {
        api.savePracticeSession(full).catch((err) => {
            // Not lost: parked on the device and imported on the next visit
            console.warn("[progress] Failed to save the practice, keeping it on this device", err);
            if (entry.counted) storage.savePracticeLog([...storage.loadPracticeLog(), full]);
        });
    } else if (entry.counted) {
        storage.savePracticeLog(log);
    }
    publish(isLoggedIn() ? "server" : "local");
    return { before, after: store.get('progress.summary') };
}

export function getSummary() {
    return store.get('progress.summary') || computeProgress([]);
}

export function completedCount() {
    return getSummary().completedCount;
}
